import type { IntelligenceContext, IntelligenceRequest, SavedIntelligenceRecord, SportId } from '../contracts/ccos';
import { parseIntelligenceContext, parseIntelligenceRequest, parseSavedIntelligence } from '../contracts/validation.ts';
import type { ValidationIssue } from '../contracts/schema';

/** Trusted relationship data supplied by the caller, never by a request body. */
export interface ContextCatalog {
  users: readonly { id: string }[];
  programs: readonly { id: string; sports: readonly SportId[]; seasons: readonly number[] }[];
  coaches: readonly { id: string; program_id: string; sport: SportId; seasons: readonly number[] }[];
  athletes: readonly { id: string; user_ids: readonly string[]; sports: readonly SportId[]; positions: readonly string[] }[];
  positions: Readonly<Record<SportId, readonly string[]>>;
}
export type ContextValidation<T = IntelligenceContext> =
  | { ok: true; value: T; warnings: ValidationIssue[] }
  | { ok: false; issues: ValidationIssue[] };

/** Validity does not imply authentication, entitlement or intelligence availability. */
export function validateContext(input: unknown, catalog: ContextCatalog): ContextValidation {
  const parsed = parseIntelligenceContext(input);
  if (!parsed.ok) return parsed;
  const context = parsed.value;
  const issues: ValidationIssue[] = [];
  const add = (field: string, code: string, message: string) => issues.push({ path: `$.${field}`, code, message });
  if (!catalog.users.some(user => user.id === context.user_id)) add('user_id', 'unknown_user', 'User is absent from the supplied context catalog.');
  const program = catalog.programs.find(item => item.id === context.program_id);
  if (!program) add('program_id', 'unknown_program', 'Program is absent from the context catalog.');
  else {
    if (!program.sports.includes(context.sport)) add('sport', 'unsupported_program_sport', 'This sport is not supported for the program.');
    if (!program.seasons.includes(context.season)) add('season', 'unsupported_season', 'This season is not supported by the context catalog; no other season was substituted.');
  }
  if (context.position && !catalog.positions[context.sport].includes(context.position)) add('position', 'invalid_sport_position', 'Position does not belong to the selected sport.');
  if (context.athlete_id) {
    const athlete = catalog.athletes.find(item => item.id === context.athlete_id);
    if (!athlete) add('athlete_id', 'unknown_athlete', 'Athlete is absent from the context catalog.');
    else {
      if (!athlete.user_ids.includes(context.user_id)) add('athlete_id', 'athlete_user_mismatch', 'The catalog does not relate this athlete to this user.');
      if (!athlete.sports.includes(context.sport)) add('athlete_id', 'athlete_sport_mismatch', 'Athlete does not participate in the selected sport.');
      if (context.position && !athlete.positions.includes(context.position)) add('position', 'athlete_position_mismatch', 'Position does not match the athlete profile.');
    }
  }
  if (issues.length) return { ok: false, issues };
  const warnings: ValidationIssue[] = [];
  const supportedCoach = context.coach_id !== null && catalog.coaches.some(coach =>
    coach.id === context.coach_id && coach.program_id === context.program_id &&
    coach.sport === context.sport && coach.seasons.includes(context.season));
  if (context.coach_id !== null && !supportedCoach) warnings.push({
    path: '$.coach_id', code: 'coach_unavailable',
    message: 'Coach is not supported for this program/sport/season; normalized to null.'
  });
  return { ok: true, value: { ...context, coach_id: supportedCoach ? context.coach_id : null }, warnings };
}

/** Validates request shape and its context only; does not authorize or dispatch. */
export function validateRequestContext(input: unknown, catalog: ContextCatalog): ContextValidation<IntelligenceRequest> {
  const parsed = parseIntelligenceRequest(input);
  if (!parsed.ok) return parsed;
  const context = validateContext(parsed.value.context, catalog);
  if (!context.ok) return { ok: false, issues: context.issues.map(issue => ({ ...issue, path: issue.path.replace('$', '$.context') })) };
  return { ok: true, value: { ...parsed.value, context: context.value }, warnings: context.warnings.map(issue => ({ ...issue, path: issue.path.replace('$', '$.context') })) };
}

/** Validates the stored season, never replaces it with current UI context. */
export function validateSavedContext(input: unknown, catalog: ContextCatalog): ContextValidation<SavedIntelligenceRecord> {
  const parsed = parseSavedIntelligence(input);
  if (!parsed.ok) return parsed;
  const context = validateContext(parsed.value.context_snapshot, catalog);
  if (!context.ok) return { ok: false, issues: context.issues.map(issue => ({ ...issue, path: issue.path.replace('$', '$.context_snapshot') })) };
  return { ok: true, value: { ...parsed.value, context_snapshot: context.value }, warnings: context.warnings.map(issue => ({ ...issue, path: issue.path.replace('$', '$.context_snapshot') })) };
}
